// Ajustes (#1521): popup con submenús, opción A del mockup
// tools/mockups/smartdx_mockups_ajustes_popup.html (calcado del de Claude).
//
// Reemplaza al diálogo chico de Ajustes y al link a /perfil: el engranaje y el
// avatar del navbar abren ESTE popup, cada uno en su sección. La página /perfil
// sigue viva para los links directos (verificación, gate de perfil incompleto)
// y "Editar perfil" lleva a /perfil/editar, que ya existe.
//
// Escritorio: sidebar fija + panel. Celular (≤768): pantalla completa, primero
// la lista de submenús y cada uno entra con flecha atrás, como en el mockup.
//
// Todo lo clickeable es ButtonBase y no Button: el theme global le pinta hover
// teal sólido a cada Button (theme.js → MuiButton), y aquí las filas son
// discretas. Sin ripple (dejaba una mancha en el ítem recién elegido); el foco
// de teclado se marca con `focoSx`.
import React from "react";
import {
  Avatar,
  Box,
  ButtonBase,
  Chip,
  Dialog,
  IconButton,
  InputBase,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import Logout from "@mui/icons-material/Logout";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useNavigate } from "react-router";

import { useCurrentUser } from "../context/UserContext";
import { useThemeMode } from "../context/ThemeModeContext";
import { logout } from "../../helpers/authentication-helpers";
import { WHATSAPP_SOPORTE } from "../../helpers/soporte";
import { modulosEncendidos } from "../navbars/herramientas";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { adoptRotatedToken, revokeOtherSessions } from "../../helpers/password-reset-api";
import { isStandalone, openInstallGuide } from "../../helpers/pwa-install";
import { PROFESSIONS } from "../../helpers/profesiones";
import { pideVerificacion } from "../../helpers/pasos-verificacion";
import ChangePasswordPanel from "../navbars/ChangePasswordPanel";
import VerificationStatusBadge from "../../views/profile/VerificationStatusBadge";
import { DirectoryPreview, SuscripcionPanel } from "../../views/profile/ProfileView";


const focoSx = { "&.Mui-focusVisible": { outline: "2px solid var(--color-primary-soft)", outlineOffset: 1 } };

// `claves` alimenta el buscador: lo que alguien escribiría para llegar ahí.
export const SECCIONES = [
  { id: "general", label: "General", Icon: SettingsOutlinedIcon, grupo: "Configuración", claves: "tema apariencia oscuro claro sistema instalar app celular" },
  { id: "perfil", label: "Perfil", Icon: PersonOutlineIcon, grupo: "Configuración", claves: "nombre foto firma cargo bio profesion registro editar" },
  { id: "cuenta", label: "Cuenta y seguridad", Icon: ShieldOutlinedIcon, grupo: "Configuración", claves: "clave contraseña password sesiones correo email salir cerrar sesion" },
  { id: "suscripcion", label: "Suscripción", Icon: CreditCardOutlinedIcon, grupo: "Configuración", claves: "plan pago pagos uso semanal boleta factura prueba" },
  { id: "verificacion", label: "Verificación y módulos", Icon: VerifiedOutlinedIcon, grupo: "Configuración", claves: "verificacion carnet sis modulos acceso permisos" },
  { id: "colegas", label: "Comunidad", Icon: GroupOutlinedIcon, grupo: "SmartDx", claves: "comunidad directorio colegas medicos globo" },
  { id: "ayuda", label: "Ayuda", Icon: HelpOutlineIcon, grupo: "SmartDx", claves: "ayuda soporte correo dudas contacto" },
];

const sinTildes = (s) => (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export const filtrarSecciones = (texto) => {
  const q = sinTildes(texto).trim();
  if (!q) return SECCIONES;
  return SECCIONES.filter((s) => sinTildes(`${s.label} ${s.claves}`).includes(q));
};

// ── Piezas de presentación ───────────────────────────────────────────────

const Titulo = ({ children, primero = false }) => (
  <Typography sx={{ fontSize: 16, fontWeight: 650, color: "var(--text-primary)", mt: primero ? 0 : 3.25, mb: 0.75 }}>
    {children}
  </Typography>
);

// Fila "etiqueta a la izquierda, control a la derecha".
const Fila = ({ label, sub, children, testId }) => (
  <Box
    data-testid={testId}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      // En el celular el control baja bajo el texto en vez de apretarlo a una
      // columna de 3 palabras ("Cerrar las otras sesiones" no cabe al lado).
      flexWrap: "wrap",
      columnGap: 2,
      rowGap: 1.25,
      py: 1.75,
      borderBottom: "1px solid var(--border-soft)",
      "&:last-of-type": { borderBottom: 0 },
    }}
  >
    <Box sx={{ minWidth: 0, flex: "1 1 220px" }}>
      <Typography sx={{ fontSize: 14, color: "var(--text-primary)" }}>{label}</Typography>
      {sub && (
        <Typography sx={{ fontSize: 12.5, color: "var(--text-muted)", mt: 0.25, overflowWrap: "anywhere" }}>
          {sub}
        </Typography>
      )}
    </Box>
    {children && <Box sx={{ flex: "0 0 auto" }}>{children}</Box>}
  </Box>
);

// Botón con borde, sin el relleno teal del theme global.
const BotonBorde = ({ children, icon, onClick, peligro = false, disabled = false, testId, sx }) => (
  <ButtonBase
    disableRipple
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      minHeight: 36,
      px: 1.5,
      borderRadius: "9px",
      border: "1px solid",
      borderColor: peligro ? "rgba(var(--color-danger-rgb),0.35)" : "var(--border-strong)",
      color: peligro ? "var(--color-danger)" : "var(--text-primary)",
      fontSize: 13,
      fontWeight: 500,
      whiteSpace: "nowrap",
      transition: "background-color 120ms cubic-bezier(0.22,1,0.36,1)",
      "&:hover": { backgroundColor: peligro ? "rgba(var(--color-danger-rgb),0.08)" : "rgba(var(--fg-rgb),0.05)" },
      "&.Mui-disabled": { opacity: 0.55 },
      "& svg": { fontSize: 17 },
      ...focoSx,
      ...sx,
    }}
  >
    {icon}
    {children}
  </ButtonBase>
);

const Enlace = ({ children, onClick, testId }) => (
  <ButtonBase
    disableRipple
    onClick={onClick}
    data-testid={testId}
    sx={{
      color: "var(--color-primary-soft)",
      fontWeight: 600,
      fontSize: 13.5,
      borderRadius: "6px",
      px: 0.75,
      py: 0.5,
      "&:hover": { backgroundColor: "rgba(var(--fg-rgb),0.05)" },
      ...focoSx,
    }}
  >
    {children}
  </ButtonBase>
);

// Tema: Sistema / Claro / Oscuro, segmentado de 3 íconos como el de Claude.
const OPCIONES_TEMA = [
  { valor: "system", label: "Sistema", Icon: DesktopWindowsOutlinedIcon },
  { valor: "light", label: "Claro", Icon: LightModeOutlinedIcon },
  { valor: "dark", label: "Oscuro", Icon: DarkModeOutlinedIcon },
];

const SelectorTema = () => {
  const { preference, setMode } = useThemeMode();
  return (
    <Box
      role="radiogroup"
      aria-label="Tema"
      sx={{ display: "inline-flex", gap: "2px", p: "3px", borderRadius: "10px", backgroundColor: "var(--bg-input)" }}
    >
      {OPCIONES_TEMA.map(({ valor, label, Icon }) => {
        const on = preference === valor;
        return (
          <ButtonBase
            disableRipple
            key={valor}
            role="radio"
            aria-checked={on}
            aria-label={label}
            title={label}
            data-testid={`ajustes-tema-${valor}`}
            onClick={() => setMode(valor)}
            sx={{
              width: 38,
              height: 32,
              borderRadius: "8px",
              color: on ? "var(--text-primary)" : "var(--text-muted)",
              backgroundColor: on ? "var(--bg-elevated)" : "transparent",
              boxShadow: on ? "0 1px 3px rgba(var(--shadow-rgb),0.25)" : "none",
              "&:hover": on ? {} : { color: "var(--text-primary)" },
              "& svg": { fontSize: 18 },
              ...focoSx,
            }}
          >
            <Icon />
          </ButtonBase>
        );
      })}
    </Box>
  );
};

// ── Secciones ────────────────────────────────────────────────────────────

const SeccionGeneral = ({ nombre, email }) => (
  <>
    <Titulo primero>Apariencia</Titulo>
    <Fila label="Tema" testId="ajustes-fila-tema">
      <SelectorTema />
    </Fila>
    {!isStandalone() && (
      <>
        <Titulo>Aplicación</Titulo>
        <Fila label="Instalar en el celular" sub="Acceso directo en la pantalla de inicio, sin barra del navegador.">
          <BotonBorde icon={<PhoneIphoneOutlinedIcon />} onClick={openInstallGuide}>
            Ver cómo
          </BotonBorde>
        </Fila>
      </>
    )}
    <Titulo>Sesión</Titulo>
    <Fila label={nombre || "Mi cuenta"} sub={email}>
      <BotonBorde peligro icon={<Logout />} onClick={logout} testId="ajustes-cerrar-sesion">
        Cerrar sesión
      </BotonBorde>
    </Fila>
  </>
);

const SeccionPerfil = ({ currentUser, profile, nombre, iniciales, irA }) => {
  const status = profile?.verification_status || "NOT_SUBMITTED";
  const profesion = PROFESSIONS.find((p) => p.value === profile?.profession)?.label;
  const prefijo = (profile?.treatment || profile?.cargo || "").trim();
  return (
    <>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5, pr: 5 }}>
        <Avatar
          src={profile?.profile_photo || undefined}
          sx={{ width: 64, height: 64, fontSize: 20, fontWeight: 700, background: "linear-gradient(135deg, var(--color-primary), var(--color-primary-soft))" }}
        >
          {iniciales}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: "var(--text-primary)" }} noWrap>
            {nombre}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "var(--text-muted)" }}>
            {profesion || "Profesión sin definir"}
            {profile?.registration_number ? ` · Reg. ${profile.registration_number}` : ""}
          </Typography>
          {/* #1604: fuera de Chile no hay verificación, así que tampoco su estado. */}
          {pideVerificacion(profile) && <Box sx={{ mt: 0.75 }}><VerificationStatusBadge status={status} /></Box>}
        </Box>
      </Stack>
      <Fila label="Nombre completo" sub={profile?.full_name || "Sin nombre"} />
      <Fila label="Firma de notas" sub={prefijo ? `${prefijo} ${profile?.full_name || ""}`.trim() : "Sin cargo: firmas solo con tu nombre"} />
      <Fila label="Correo" sub={currentUser?.email} />
      <Fila label="Bio" sub={profile?.bio || "Aún no agregaste una bio."} />
      <Box sx={{ mt: 2 }}>
        <BotonBorde icon={<EditOutlinedIcon />} onClick={() => irA("/perfil/editar")} testId="ajustes-editar-perfil">
          Editar perfil completo
        </BotonBorde>
      </Box>
    </>
  );
};

const SeccionCuenta = ({ email, subVista, setSubVista }) => {
  const [cerrando, setCerrando] = React.useState(false);
  const [aviso, setAviso] = React.useState(null);

  // Cerrar las otras sesiones = ROTAR el token. DRF guarda uno solo por
  // usuario, compartido por todos los aparatos: al emitir uno nuevo, los demás
  // se quedan con el string viejo y salen. Por eso no se puede cerrar una sola
  // sesión, y por eso hay que guardar el token nuevo o este aparato también
  // queda afuera.
  const cerrarOtrasSesiones = async () => {
    setCerrando(true);
    setAviso(null);
    try {
      const { data } = await revokeOtherSessions();
      if (adoptRotatedToken(data)) {
        setAviso({ ok: true, texto: "Listo. Cerramos la sesión en tus otros aparatos." });
      } else {
        logout();
      }
    } catch {
      setAviso({ ok: false, texto: "No pudimos cerrarlas. Revisa tu conexión y prueba de nuevo." });
    } finally {
      setCerrando(false);
    }
  };

  if (subVista === "clave") {
    return (
      <>
        <Titulo primero>Cambiar clave</Titulo>
        <ChangePasswordPanel email={email} onDone={() => setSubVista(null)} />
      </>
    );
  }

  return (
    <>
      <Titulo primero>Cuenta</Titulo>
      <Fila label="Correo" sub={email} />
      <Titulo>Seguridad</Titulo>
      <Fila label="Clave" sub="Cámbiala si crees que alguien más la conoce.">
        <BotonBorde icon={<LockOutlinedIcon />} onClick={() => setSubVista("clave")}>
          Cambiar clave
        </BotonBorde>
      </Fila>
      <Fila label="Otros aparatos" sub="Si dejaste la cuenta abierta en otro lado. En este aparato te quedas dentro.">
        <BotonBorde icon={<DevicesOutlinedIcon />} onClick={cerrarOtrasSesiones} disabled={cerrando}>
          {cerrando ? "Cerrando..." : "Cerrar las otras sesiones"}
        </BotonBorde>
      </Fila>
      {aviso && (
        <Typography
          sx={{ fontSize: 12.5, mt: 0.5, color: aviso.ok ? "var(--color-success)" : "var(--color-danger)" }}
        >
          {aviso.texto}
        </Typography>
      )}
      <Titulo>Sesión</Titulo>
      <Fila label="Este aparato" sub="Sales de SmartDx aquí.">
        <BotonBorde peligro icon={<Logout />} onClick={logout}>
          Cerrar sesión
        </BotonBorde>
      </Fila>
    </>
  );
};

const SeccionVerificacion = ({ currentUser, profile, irA }) => {
  const status = profile?.verification_status || "NOT_SUBMITTED";
  // #1681 D10/D58: solo los encendidos, con los nombres de la barra (una sola lista).
  const habilitados = modulosEncendidos(currentUser);
  const marca = (ok) => (
    <Typography sx={{ fontSize: 14, color: ok ? "var(--color-success)" : "var(--text-faint)" }}>{ok ? "Sí" : "No"}</Typography>
  );
  // #1604: la verificación (carnet + certificado) es solo para Chile; fuera de
  // Chile la sección muestra solo los módulos.
  const conVerificacion = pideVerificacion(profile);
  return (
    <>
      {conVerificacion && (
      <>
      <Titulo primero>Verificación</Titulo>
      <Fila label="Estado"><VerificationStatusBadge status={status} /></Fila>
      <Fila label="Carnet subido">{marca(profile?.has_id_document)}</Fila>
      <Fila label="Certificado SIS">{marca(profile?.has_health_certificate)}</Fila>
      {profile?.verified_at && (
        <Fila label="Aprobado">
          <Typography sx={{ fontSize: 14, color: "var(--text-muted)" }}>
            {new Date(profile.verified_at).toLocaleDateString("es-CL")}
          </Typography>
        </Fila>
      )}
      {(status === "NOT_SUBMITTED" || status === "REJECTED") && (
        <Box sx={{ mt: 1.5 }}>
          <BotonBorde onClick={() => irA("/perfil/verificacion")}>
            {status === "REJECTED" ? "Reenviar verificación" : "Completar verificación"}
          </BotonBorde>
        </Box>
      )}
      </>
      )}
      <Titulo primero={!conVerificacion}>Módulos habilitados</Titulo>
      {habilitados.length === 0 && (
        <Typography sx={{ fontSize: 13, color: "var(--text-muted)", mb: 1 }}>
          Aún no tienes módulos habilitados. Se activan al aprobar tu verificación.
        </Typography>
      )}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.25 }}>
        {habilitados.map((m) => (
          <Chip
            key={m.flag}
            label={m.label}
            size="small"
            sx={{ height: 26, fontSize: 12, backgroundColor: "rgba(var(--color-success-rgb),0.12)", color: "var(--color-success)" }}
          />
        ))}
      </Box>
      <Typography sx={{ fontSize: 12, color: "var(--text-faint)", mt: 2.75 }}>
        ¿Necesitas otro módulo?{" "}
        <a
          data-testid="ajustes-modulos-whatsapp"
          href={WHATSAPP_SOPORTE}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "var(--color-success)", fontWeight: 600, textDecoration: "underline", display: "inline-flex", alignItems: "center", gap: 4 }}
        >
          <WhatsAppIcon sx={{ fontSize: 14 }} />
          Escríbenos por WhatsApp
        </a>
      </Typography>
    </>
  );
};

const SeccionColegas = ({ irA }) => (
  <>
    <Titulo primero>Comunidad</Titulo>
    <Typography sx={{ fontSize: 12.5, color: "var(--text-muted)", mb: 2 }}>
      Otros profesionales verificados en SmartDx.
    </Typography>
    <DirectoryPreview onNavigate={irA} />
    <Box sx={{ mt: 2 }}>
      {/* #1301: Colegas pasó a Comunidad; /directorio solo redirige. */}
      <Enlace onClick={() => irA("/comunidad")}>Ver la Comunidad</Enlace>
    </Box>
  </>
);

const SeccionAyuda = () => (
  <>
    <Titulo primero>Ayuda</Titulo>
    {/* #1681 D62: los médicos hablan por WhatsApp; sin correo ni mailto en Ayuda. */}
    <Fila label="Escríbenos" sub="Respondemos por WhatsApp">
      <BotonBorde
        testId="ajustes-ayuda-whatsapp"
        icon={<WhatsAppIcon />}
        onClick={() => window.open(WHATSAPP_SOPORTE, "_blank", "noopener,noreferrer")}
      >
        Escribir por WhatsApp
      </BotonBorde>
    </Fila>
  </>
);

// ── Navegación ───────────────────────────────────────────────────────────

const ItemNav = ({ seccion, activo, onClick, movil }) => {
  const { Icon } = seccion;
  return (
    <ButtonBase
      disableRipple
      onClick={onClick}
      data-testid={`ajustes-nav-${seccion.id}`}
      aria-current={activo ? "page" : undefined}
      sx={{
        width: "100%",
        justifyContent: "flex-start",
        gap: movil ? 1.5 : 1.25,
        px: movil ? 1 : 1.25,
        py: movil ? 1.25 : 1,
        minHeight: movil ? 52 : 38,
        borderRadius: movil ? "10px" : "9px",
        fontSize: movil ? 14.5 : 14,
        fontWeight: activo ? 600 : 400,
        color: activo ? "var(--text-primary)" : "var(--text-secondary)",
        backgroundColor: activo ? "rgba(var(--fg-rgb),0.08)" : "transparent",
        textAlign: "left",
        "&:hover": { backgroundColor: activo ? "rgba(var(--fg-rgb),0.08)" : "rgba(var(--fg-rgb),0.05)" },
        ...focoSx,
      }}
    >
      {movil ? (
        <Box sx={{ width: 32, height: 32, borderRadius: "9px", display: "grid", placeItems: "center", flex: "0 0 auto", backgroundColor: "rgba(var(--color-primary-rgb),0.14)", color: "var(--color-primary-soft)" }}>
          <Icon sx={{ fontSize: 18 }} />
        </Box>
      ) : (
        <Icon sx={{ fontSize: 18 }} />
      )}
      <Box component="span" sx={{ flex: 1 }}>{seccion.label}</Box>
      {movil && <ChevronRightIcon sx={{ fontSize: 18, color: "var(--text-faint)" }} />}
    </ButtonBase>
  );
};

const Buscador = ({ valor, onChange, sx }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      px: 1.25,
      height: 38,
      borderRadius: "10px",
      border: "1px solid var(--border-strong)",
      color: "var(--text-faint)",
      ...sx,
    }}
  >
    <SearchIcon sx={{ fontSize: 18 }} />
    <InputBase
      value={valor}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Buscar"
      inputProps={{ "aria-label": "Buscar en ajustes", "data-testid": "ajustes-buscar" }}
      sx={{ flex: 1, fontSize: 13.5, color: "var(--text-primary)" }}
    />
  </Box>
);

const ListaNav = ({ secciones, activa, onElegir, movil }) => {
  if (secciones.length === 0) {
    return (
      <Typography sx={{ fontSize: 13, color: "var(--text-faint)", px: 1.25, py: 1 }}>
        Sin resultados.
      </Typography>
    );
  }
  const grupos = [...new Set(secciones.map((s) => s.grupo))];
  return grupos.map((g, i) => (
    <Box key={g}>
      {movil ? (
        i > 0 && <Box sx={{ height: "1px", backgroundColor: "var(--border-soft)", mx: 1, my: 0.75 }} />
      ) : (
        <Typography sx={{ fontSize: 11.5, color: "var(--text-faint)", mx: 1.25, mt: i === 0 ? 0 : 1.75, mb: 0.75 }}>
          {g}
        </Typography>
      )}
      <Stack spacing={movil ? 0 : 0.25}>
        {secciones.filter((s) => s.grupo === g).map((s) => (
          <ItemNav key={s.id} seccion={s} activo={!movil && s.id === activa} onClick={() => onElegir(s.id)} movil={movil} />
        ))}
      </Stack>
    </Box>
  ));
};

const BotonIcono = ({ label, onClick, children, sx }) => (
  <IconButton
    aria-label={label}
    onClick={onClick}
    sx={{
      width: 36,
      height: 36,
      borderRadius: "9px",
      color: "var(--text-muted)",
      background: "transparent",
      "&:hover": { backgroundColor: "rgba(var(--fg-rgb),0.08)", color: "var(--text-primary)" },
      ...sx,
    }}
  >
    {children}
  </IconButton>
);

// ── Diálogo ──────────────────────────────────────────────────────────────

/**
 * @param {string} seccionInicial  sección al abrir; en el celular, null = la lista.
 * @param {string} subVistaInicial "clave" abre directo Cambiar clave (dentro de Cuenta).
 */
const AjustesDialog = ({ open, onClose, seccionInicial = "general", subVistaInicial = null }) => {
  const movil = useMediaQuery("(max-width: 768px)");
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  // El estado nace de las props y no se resetea con un efecto: quien abre el
  // popup le cambia la `key` en cada apertura, así arranca donde la pidió
  // (engranaje → General, avatar → Perfil). Con un efecto, el primer cuadro
  // mostraba la sección de la vez anterior.
  const [seccion, setSeccion] = React.useState(seccionInicial);
  const [subVista, setSubVista] = React.useState(subVistaInicial);
  const [busqueda, setBusqueda] = React.useState("");

  const profile = currentUser?.professional_profile;
  const nombre = profile?.full_name || currentUser?.username || currentUser?.email || "";
  const iniciales = (nombre || "?").trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("");
  const email = currentUser?.email || "";

  const irA = (ruta) => {
    onClose();
    navigate(ruta);
  };
  const elegir = (id) => {
    setSeccion(id);
    setSubVista(null);
  };

  // #1604: fuera de Chile la sección de verificación solo muestra los módulos.
  const conEtiqueta = (s) =>
    s && s.id === "verificacion" && !pideVerificacion(profile) ? { ...s, label: "Módulos habilitados" } : s;
  const secciones = filtrarSecciones(busqueda).map(conEtiqueta);
  const actual = conEtiqueta(SECCIONES.find((s) => s.id === seccion)) || null;

  const contenido = (() => {
    switch (actual?.id) {
      case "general": return <SeccionGeneral nombre={nombre} email={email} />;
      case "perfil": return <SeccionPerfil currentUser={currentUser} profile={profile} nombre={nombre} iniciales={iniciales} irA={irA} />;
      case "cuenta": return <SeccionCuenta email={email} subVista={subVista} setSubVista={setSubVista} />;
      case "suscripcion":
        return (
          // Sin <Titulo>: el panel trae su propio encabezado con el estado
          // ("Al día", "En prueba"), y dos títulos seguidos se leían repetidos.
          <Box sx={{ pr: 5 }}>
            <SuscripcionPanel
              sinMarco
              suscripcion={currentUser?.suscripcion}
              access={currentUser?.access}
              trialEndsAt={currentUser?.trial_ends_at}
            />
          </Box>
        );
      case "verificacion": return <SeccionVerificacion currentUser={currentUser} profile={profile} irA={irA} />;
      case "colegas": return <SeccionColegas irA={irA} />;
      case "ayuda": return <SeccionAyuda />;
      default: return null;
    }
  })();

  // Volver: de Cambiar clave a Cuenta; en el celular, de la sección a la lista.
  const puedeVolver = subVista !== null || (movil && actual !== null);
  const volver = () => {
    if (subVista !== null) setSubVista(null);
    else setSeccion(null);
  };

  if (movil) {
    const titulo = subVista === "clave" ? "Cambiar clave" : actual?.label || "Ajustes";
    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        PaperProps={{ "data-testid": "ajustes-dialog", sx: { backgroundColor: "var(--bg-elevated)" } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.75, pt: "12px", pb: 1.25, borderBottom: "1px solid var(--border-soft)" }}>
          {puedeVolver && (
            <BotonIcono label="Volver" onClick={volver} sx={{ border: "1px solid var(--border-strong)", color: "var(--text-primary)" }}>
              <ChevronLeftIcon fontSize="small" />
            </BotonIcono>
          )}
          <Typography sx={{ flex: 1, fontSize: 16, fontWeight: 700, color: "var(--text-primary)" }}>{titulo}</Typography>
          <BotonIcono label="Cerrar ajustes" onClick={onClose}><CloseIcon fontSize="small" /></BotonIcono>
        </Box>
        <Box sx={{ flex: 1, overflowY: "auto", px: actual ? 2.5 : 1.25, py: actual ? 2.5 : 1, pb: "calc(env(safe-area-inset-bottom) + 24px)" }}>
          {actual ? (
            contenido
          ) : (
            <>
              <Buscador valor={busqueda} onChange={setBusqueda} sx={{ mx: 0.5, mt: 0.75, mb: 1.25 }} />
              <ListaNav secciones={secciones} onElegir={elegir} movil />
            </>
          )}
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        "data-testid": "ajustes-dialog",
        sx: {
          width: 820,
          maxWidth: "calc(100vw - 32px)",
          height: 600,
          maxHeight: "calc(100vh - 48px)",
          m: 2,
          borderRadius: "16px",
          border: "1px solid var(--border-soft)",
          backgroundColor: "var(--bg-elevated)",
          boxShadow: "0 24px 64px rgba(var(--shadow-rgb),0.45)",
          display: "grid",
          gridTemplateColumns: "230px 1fr",
          overflow: "hidden",
        },
      }}
    >
      <Box
        component="nav"
        aria-label="Secciones de ajustes"
        sx={{ backgroundColor: "var(--bg-base)", borderRight: "1px solid var(--border-soft)", p: "16px 12px", overflowY: "auto" }}
      >
        <Buscador valor={busqueda} onChange={setBusqueda} sx={{ mb: 2 }} />
        <ListaNav secciones={secciones} activa={seccion} onElegir={elegir} />
      </Box>
      <Box sx={{ position: "relative", overflowY: "auto", p: "26px 30px 28px", scrollbarGutter: "stable" }}>
        <Box sx={{ position: "absolute", top: 14, right: 14, display: "flex", gap: 0.5 }}>
          {puedeVolver && (
            <BotonIcono label="Volver" onClick={volver}><ChevronLeftIcon fontSize="small" /></BotonIcono>
          )}
          <BotonIcono label="Cerrar ajustes" onClick={onClose}><CloseIcon fontSize="small" /></BotonIcono>
        </Box>
        {contenido}
      </Box>
    </Dialog>
  );
};

export default AjustesDialog;
